import { Link } from "@tanstack/react-router";
import { SafeImage } from "@/components/site/SafeImage";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { SearchDialog } from "@/components/site/SearchDialog";
import logoTransparent from "@/assets/lifewestway-logo-160.webp";


const LINKS = [
  { to: "/",                  label: "Home",         tip: "LifeWestWay home — Western migration hub" },
  { to: "/immigration",       label: "Immigration",  tip: "Visas, PR pathways & citizenship guides" },
  { to: "/visa-guides-2026",  label: "Guides",        tip: "Curated 2026 visa guides: Portugal, Canada, UK & Australia" },
  { to: "/jobs",              label: "Jobs",         tip: "Job market intel, salaries & hiring trends" },
  { to: "/remote-work",       label: "Remote",       tip: "Remote work & job guides across all 6 countries" },
  { to: "/business",          label: "Business",     tip: "Business setup abroad — company registration, founder visas, tax & banking in all 6 countries" },
  { to: "/contact",           label: "Contact",      tip: "Get in touch with the LifeWestWay team" },
] as const;

// Second rail — money, health & story hubs
const SECONDARY_LINKS = [
  { to: "/healthcare",          label: "Health",      tip: "Health care & insurance guides for all 6 countries" },
  { to: "/cross-border-wealth", label: "Wealth",      tip: "Cross-Border Wealth & Offshore Banking — legal disclosure guides for all 6 countries" },
  { to: "/finance",           label: "Finance",      tip: "Banking, credit & personal finance abroad" },
  { to: "/credit-cards",      label: "Credit Cards", tip: "Best newcomer & expat credit cards by country" },
  { to: "/housing-living-cost", label: "Living Cost", tip: "Rent, deposits, bills & monthly budgets by country" },
  { to: "/investment-money-transfer", label: "Investment", tip: "Investing abroad & cheapest cross-border transfers by country" },
  { to: "/tax",               label: "Tax",          tip: "Tax residency, filing rules & optimisation" },
  { to: "/success-stories",   label: "Success",      tip: "Real expat visa, job & PR success journeys by country" },
] as const;

// Site information pages — must be reachable from any page
const UTILITY_LINKS = [
  { to: "/search",  label: "Search",         tip: "Search every LifeWestWay guide by keyword, country or category" },
  { to: "/about",   label: "About Us",       tip: "Learn about LifeWestWay's editorial team" },
  { to: "/privacy", label: "Privacy Policy", tip: "How we handle your data" },
  { to: "/terms",   label: "Terms",          tip: "Terms of use for LifeWestWay" },
] as const;

const ALL_LINKS = [...LINKS, ...SECONDARY_LINKS, ...UTILITY_LINKS];



export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      role="banner"
      className={`sticky top-0 z-50 w-full transition-all ${
        scrolled
          ? "bg-background/85 backdrop-blur border-b border-border shadow-[0_1px_0_rgba(0,0,0,0.4)]"
          : "bg-transparent"
      }`}

    >
      <a title="Skip to the main content of this page"
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded-md focus:bg-navy focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
      >
        Skip to main content
      </a>
      <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
        {/* Unified nav rail — one continuous pill from logo to Contact */}
        <div className={`relative flex h-14 items-center gap-2 rounded-full border pl-2 pr-2 shadow-[0_10px_30px_-14px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:pl-3 sm:pr-3 transition-colors ${scrolled ? "border-border/70 bg-card/70" : "border-white/15 bg-black/25"}`}>
          {/* Gold hairline accent ring */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-accent/20"
          />

          {/* Logo lockup — transparent, no background, no effects */}
          <Link
            to="/"
            aria-label="LifeWestWay — home"
            title="LifeWestWay — home"
            className="relative -ml-1 flex min-w-0 shrink items-center gap-3 pr-2 sm:-ml-2 sm:gap-4 sm:pr-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span className="grid h-14 w-14 sm:h-16 sm:w-16 shrink-0 place-items-center">
              <SafeImage
                src={logoTransparent}
                alt="LifeWestWay logo — global expat lifestyle and career hub"
                width={160}
                height={160}
                eager
                noSkeleton
                className="h-full w-full object-contain"
              />
            </span>
            <span className="whitespace-nowrap font-display text-2xl sm:text-3xl lg:text-[2rem] font-bold tracking-tight text-foreground">
              LifeWest<span className="text-accent">Way</span>
            </span>

          </Link>

          {/* Flexible spacer pushes nav to the right */}
          <div className="hidden md:flex flex-1" aria-hidden />


          {/* Nav links — same pill, more breathing room */}
          <nav
            aria-label="Primary"
            className="relative hidden md:flex items-center gap-1 lg:gap-1.5"
          >
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                title={l.tip}
                className="nav-pill tap-target"
                activeProps={{
                  className:
                    "!text-accent-foreground !bg-accent shadow-[0_4px_14px_-4px_rgba(0,0,0,0.6)] ring-1 ring-inset ring-accent/60",
                  "aria-current": "page",
                }}
                activeOptions={{ exact: l.to === "/" }}
              >
                {l.label}
              </Link>
            ))}
          </nav>


          {/* Search — desktop */}
          <div className="hidden md:block ml-1">
            <SearchDialog />
          </div>

          {/* Mobile: push burger to the right */}
          <div className="md:hidden flex-1" aria-hidden />
          <div className="md:hidden">
            <SearchDialog />
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full bg-navy text-white ring-1 ring-inset ring-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            aria-label={open ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={open}
            aria-controls="mobile-nav"
            title={open ? "Close menu" : "Open menu"}
          >
            {open ? <X aria-hidden="true" className="h-4 w-4" /> : <Menu aria-hidden="true" className="h-4 w-4" />}
          </button>
        </div>

        {/* Secondary rail — money & story hubs (premium, full-width, gold-halo) */}
        <div className={`relative mt-2 hidden md:flex w-full items-center rounded-full border px-2 py-1.5 shadow-[0_10px_30px_-14px_rgba(0,0,0,0.6)] backdrop-blur-xl transition-colors ${scrolled ? "border-border/70 bg-card/70" : "border-white/15 bg-black/40"}`}>
          <span aria-hidden className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-accent/20" />
          <span aria-hidden className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-accent/10 to-transparent" />
          <nav aria-label="Secondary" className="relative grid w-full grid-cols-4 gap-1 lg:grid-cols-8">
            {SECONDARY_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                title={l.tip}
                className="group nav-rail-link"
                activeProps={{
                  className:
                    "!text-accent-foreground !bg-accent shadow-[0_4px_14px_-4px_rgba(0,0,0,0.6)] ring-1 ring-inset ring-accent/60",
                  "aria-current": "page",
                }}
              >
                <span className="luxe-halo" />
                <span className="relative z-10">{l.label}</span>
              </Link>
            ))}
          </nav>
        </div>


      </div>

      {open && (
        <div id="mobile-nav" className="md:hidden border-t border-border bg-background">
          <nav aria-label="Mobile primary" className="mx-auto flex max-w-7xl flex-col px-4 py-3 sm:px-6">
            {ALL_LINKS.map((l) => (

              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                title={l.tip}
                className="flex items-center py-3 px-2 text-base font-medium text-foreground/80 focus-visible:outline-none focus-visible:text-accent border-b border-border/50 last:border-0"
                activeProps={{ className: "text-accent font-bold", "aria-current": "page" }}
                activeOptions={{ exact: l.to === "/" }}
              >
                {l.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}

